import { channelIconName } from '@/components/ui/Icon';
import { formatRelative } from '@/utils/formatters';
import type { CrmInteractionDto, SessionDto } from '@/types';

/** Maps a CRM interaction type onto the icon glyph that represents it. */
const INTERACTION_ICON: Record<string, string> = {
  call: 'phone',
  email: 'mail',
  chat: 'msgcircle',
  case: 'flag',
};

/** One row in the merged Tickets + Conversations history list. */
export type HistoryRow = {
  id: string;
  source: 'ticket' | 'conversation';
  icon: string;
  title: string;
  sub: string;
  atMs: number;
  /**
   * The session this row opens, set ONLY for `source === 'conversation'`.
   *
   * Absent for tickets on purpose: a CRM interaction is a record in someone else's system with no
   * conversation of ours behind it, so there is nothing to navigate to. That absence is what makes
   * ticket rows correctly render as non-interactive.
   */
  sessionId?: string;
  /**
   * The EARLIER conversation this one continues (ADR-033 D7), for a conversation row only: the server-stamped
   * `previousSessionId`, verbatim. Never inferred - see {@link buildHistoryRows}.
   */
  continues?: string;
  /**
   * The LATER conversation that continues this one, for a conversation row only: the id of the row (or the
   * `successors` claim) whose server-stamped `previousSessionId` equals this row's id. Never inferred.
   */
  continuedIn?: string;
};

/** What the console may know of a conversation that is NOT a row of the list but may continue one that is. */
export type SuccessorClaim = { sessionId: string; previousSessionId?: string | null };

/** A server-stamped id, trimmed; blank / non-string = no link. */
const stampedId = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Ids are Guids and compare case-insensitively everywhere in the console (getSessionDetail does the same). */
const idKey = (v: string): string => v.toLowerCase();

/**
 * The path of one conversation, with the folder context the agent is in kept (`search` is the current location's query
 * string, `?folder=...` or empty). Every "open that conversation" affordance goes through here and then through the normal
 * route, so the server's visibility verdict (a 404 = "Session not available") still decides what the agent sees.
 */
export function conversationHref(sessionId: string, search = ''): string {
  return `/conversations/${encodeURIComponent(sessionId)}${search}`;
}

/**
 * Merges CRM interactions (tickets/calls/emails) with this system's own session history into one
 * reverse-chronological list.
 *
 * Extracted from the old customer panel unchanged except for two removals, both of which existed
 * only because the panel was a 312px rail:
 *  - <b>no 8-row cap</b> — the contact page's whole purpose is showing ALL of a contact's history;
 *  - <b>no exclude-the-open-conversation rule</b> — a contact page has no "currently open"
 *    conversation, and hiding one arbitrary row would make the list quietly incomplete.
 *
 * <b>Linking (D7).</b> A returning customer's new conversation carries `previousSessionId`: the CLOSED conversation the
 * SERVER linked it to (a closed conversation is terminal - there is no Reopen). A row is marked `continues` from its own
 * `previousSessionId`, and a row is marked `continuedIn` when another conversation's `previousSessionId` names it. That
 * is ALL the marking reads: never the customer id, never "the next conversation in time" - a heuristic would draw a link
 * the server never made, and an agent would trust it. `successors` lets a caller name a conversation that is not a row
 * (the one open in the console, which the past-conversations list leaves out) so the row it continues is still marked.
 *
 * Pure function, so the merge/sort/labelling is testable without the fetch and render plumbing.
 */
export function buildHistoryRows(
  interactions: CrmInteractionDto[],
  sessions: SessionDto[],
  conversationFallbackTitle: string,
  successors: readonly SuccessorClaim[] = [],
): HistoryRow[] {
  // previous-id -> the conversation that continues it. Listed rows are read oldest-first so that, were two conversations
  // ever to name the same predecessor, the EARLIEST wins deterministically; claims that are not rows come last.
  const continuedInOf = new Map<string, string>();
  const claims: SuccessorClaim[] = [
    ...[...sessions]
      .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt) || a.sessionId.localeCompare(b.sessionId))
      .map((s) => ({ sessionId: s.sessionId, previousSessionId: s.previousSessionId })),
    ...successors,
  ];
  for (const c of claims) {
    const prev = stampedId(c.previousSessionId);
    if (!prev || idKey(prev) === idKey(c.sessionId)) continue;
    if (!continuedInOf.has(idKey(prev))) continuedInOf.set(idKey(prev), c.sessionId);
  }

  const ticketRows: HistoryRow[] = interactions.map((it) => ({
    id: it.interactionId,
    source: 'ticket',
    icon: INTERACTION_ICON[(it.type || '').toLowerCase()] || 'msgcircle',
    title: it.subject || it.type,
    sub: formatRelative(it.timestamp),
    atMs: Date.parse(it.timestamp),
  }));

  const conversationRows: HistoryRow[] = sessions.map((s) => {
    const atIso = s.lastMessageAt ?? s.createdAt;
    const prev = stampedId(s.previousSessionId);
    const later = continuedInOf.get(idKey(s.sessionId));
    return {
      id: s.sessionId,
      sessionId: s.sessionId,
      source: 'conversation',
      ...(prev && idKey(prev) !== idKey(s.sessionId) ? { continues: prev } : {}),
      ...(later ? { continuedIn: later } : {}),
      icon: channelIconName(s.channelType),
      // The staff-set subject wins when present — it is the name a human chose for this
      // conversation. Only when nobody has named it do we fall back to the message previews, which
      // move as the conversation continues.
      title:
        (s.subject ?? '').trim() ||
        (s.lastMessagePreview ?? '').trim() ||
        (s.firstMessagePreview ?? '').trim() ||
        conversationFallbackTitle,
      sub: formatRelative(atIso),
      atMs: Date.parse(atIso),
    };
  });

  return [...ticketRows, ...conversationRows].sort((a, b) => b.atMs - a.atMs);
}
